import { useState, useEffect, useCallback } from 'react';

const baseUrl = () => (import.meta.env.BASE_URL || '').replace(/\/$/, '') || '';
const FALLBACK_HERO_IMAGE = `${baseUrl()}/images/video-operation/hero.png`;

const slides = [
  {
    id: 1,
    title: 'WEB DESIGN',
    subtitle: '伝わる設計からはじまるWEBサイト構築',
    description: '企画から運営まで、ビジネスを加速させるWebサイトを構築します',
    image: `${baseUrl()}/images/web_design_hero.png`,
    color: 'teal'
  },
  {
    id: 2,
    title: 'VIDEO OPERATION',
    subtitle: 'オンライン配信・ハイブリッドイベント運用',
    description: 'ライブ配信やハイブリッドイベントの映像オペレーション・技術運用をサポートします',
    image: `${baseUrl()}/images/event-hero.png`,
    color: 'violet'
  },
  {
    id: 3,
    title: 'SYSTEM DEVELOPMENT',
    subtitle: 'システム・Webアプリケーション開発',
    description: 'Webサイトと連携したシステムやアプリケーションを包括的に構築',
    image: `${baseUrl()}/images/system_dev_hero.png`,
    color: 'orange'
  },
  {
    id: 4,
    title: 'DIGITAL SIGNAGE',
    subtitle: 'デジタルサイネージシステム構築',
    description: '店舗やイベント空間を彩るデジタルコンテンツを制作',
    image: `${baseUrl()}/images/digital-signage-hero.png`,
    color: 'green'
  },
  {
    id: 5,
    title: 'GRAPHIC DESIGN',
    subtitle: 'ブランディング・マーケティング素材',
    description: 'ビジュアルアイデンティティからプロモーション素材まで',
    image: `${baseUrl()}/images/graphic_design_hero.png`,
    color: 'pink'
  }
];

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageFailed, setImageFailed] = useState<Set<number>>(new Set());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleImageError = useCallback((index: number) => {
    setImageFailed((prev) => new Set(prev).add(index));
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const getColorClass = (color: string) => {
    const colors: Record<string, string> = {
      teal: 'bg-teal-500',
      orange: 'bg-orange-500',
      green: 'bg-green-500',
      pink: 'bg-pink-500',
      violet: 'bg-violet-500'
    };
    return colors[color] || 'bg-teal-500';
  };

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden">
      <div className="relative h-screen">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            <div className="absolute inset-0 bg-gray-900">
              <img
                src={imageFailed.has(index) ? FALLBACK_HERO_IMAGE : slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
                style={{
                  transform: currentSlide === index ? 'scale(1.06)' : 'scale(1)',
                  transition: 'transform 7.5s cubic-bezier(0.22, 0.61, 0.36, 1)'
                }}
                onError={() => handleImageError(index)}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50 z-[1]" />
            </div>

            <div className="relative z-10 h-full flex items-center">
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`transform transition-all duration-1000 delay-300 ${
                  currentSlide === index ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}>
                  <div className="mb-12">
                    <h1 className="text-7xl md:text-8xl font-black text-white leading-none mb-4 tracking-tight drop-shadow-2xl">
                      KUMACHOP
                    </h1>
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-1 ${getColorClass(slide.color)}`} />
                      <span className="text-sm font-medium tracking-widest uppercase text-white/90">Creative Digital Studio</span>
                    </div>
                  </div>

                  <div className="max-w-5xl">
                    <div className="mb-6">
                      <span className={`inline-block px-6 py-3 ${getColorClass(slide.color)} text-white text-sm font-bold tracking-wider uppercase`}>
                        {slide.title}
                      </span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                      {slide.subtitle}
                    </h2>
                    <p className="text-xl text-white/90 mb-10 leading-relaxed drop-shadow-lg">
                      {slide.description}
                    </p>

                    <div className="flex items-center gap-6">
                      <button
                        onClick={scrollToServices}
                        type="button"
                        className="group relative bg-white text-black px-10 py-5 font-bold text-lg hover:bg-black hover:text-white border-2 border-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        詳しく見る
                      </button>
                      <button
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        type="button"
                        className="group relative text-white px-10 py-5 font-bold text-lg border-2 border-white hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        お問い合わせ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={prevSlide}
        type="button"
        className="absolute left-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 cursor-pointer z-20"
      >
        <i className="ri-arrow-left-s-line text-3xl text-white" />
      </button>
      <button
        onClick={nextSlide}
        type="button"
        className="absolute right-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 cursor-pointer z-20"
      >
        <i className="ri-arrow-right-s-line text-3xl text-white" />
      </button>

      <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            type="button"
            className={`transition-all duration-300 cursor-pointer ${
              currentSlide === index
                ? 'w-12 h-3 bg-white rounded-full'
                : 'w-3 h-3 bg-white/50 rounded-full hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20">
        <button
          onClick={scrollToServices}
          type="button"
          className="flex flex-col items-center gap-2 group cursor-pointer"
        >
          <span className="text-xs font-medium tracking-widest uppercase text-white/80">Scroll</span>
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white rounded-full animate-bounce" />
          </div>
        </button>
      </div>

      <div className="absolute bottom-0 left-0 z-10 w-[29rem] h-[30rem] opacity-50 pointer-events-none select-none flex items-end" aria-hidden>
        <img src={`${baseUrl()}/kumachop-white.png`} alt="" className="w-full h-full object-contain object-bottom" style={{ marginLeft: '-36%', marginBottom: '-38%' }} />
      </div>
    </section>
  );
}
